/**
 * Hardcoded PAI syllabus used by the roadmap UI prototypes.
 *
 * Mirrors the "Road Map" and "Optional Going Further" columns of the course
 * outline document. This is prototype fixture data, not a source of truth:
 * the real roadmap will come from the course's roadmap documents once a
 * layout is chosen.
 */

import type {ExpeditionItem, ExpeditionWeek} from './expeditionModel';

/** Fixture alias of {@link ExpeditionItem}, kept for the older prototypes. */
export type PrototypeItem = ExpeditionItem;

/**
 * A fixture week: an {@link ExpeditionWeek} without its lock state, which
 * the gallery derives from {@link PROTOTYPE_CURRENT_WEEK_ID}.
 */
export type PrototypeWeek = Omit<ExpeditionWeek, 'isLocked'>;

export const PROTOTYPE_WEEKS: readonly PrototypeWeek[] = [
  {
    id: 'w0',
    title: 'Getting Started',
    label: 'Application',
    main: [
      {id: 'w0-prob', kind: 'learn', title: 'Probability'},
      {id: 'w0-cond', kind: 'learn', title: 'Conditional Probability'},
      {id: 'w0-textdet', kind: 'code', title: 'AI Text Detection'},
    ],
    goingFurther: [],
  },
  {
    id: 'w1',
    title: 'Rules of Chance',
    label: 'Week 1',
    date: 'Oct 9',
    instructor: 'Mehran',
    main: [
      {
        id: 'w1-axioms',
        kind: 'learn',
        title: 'Axioms, Complements & Probability of Or',
      },
      {id: 'w1-indep', kind: 'learn', title: 'Independence'},
      {
        id: 'w1-monkeys',
        kind: 'code',
        title: 'Large Language Monkeys Calculator',
        blurb: 'How many samples until a model probably gets it right?',
      },
    ],
    goingFurther: [
      {
        id: 'w1-lotp',
        kind: 'learn',
        title: 'Binary LOTP + Bayes',
        unlockedBy: 'w1-indep',
      },
      {
        id: 'w1-medbayes',
        unlockedBy: 'w1-lotp',
        kind: 'code',
        title: 'Medical Bayes',
        blurb: 'A positive test is not a diagnosis.',
      },
    ],
  },
  {
    id: 'w2',
    title: 'Random Variables',
    label: 'Week 2',
    date: 'Oct 16',
    instructor: 'Mehran',
    main: [
      {
        id: 'w2-drv',
        kind: 'learn',
        title: 'Discrete Random Variables',
        blurb: 'Bernoulli, Binomial, Geometric and Categorical as model cards.',
      },
      {id: 'w2-ev', kind: 'learn', title: 'Expectation + Variance'},
      {
        id: 'w2-artwork',
        kind: 'code',
        title: 'Probabilistic Artwork',
        blurb: 'Sample your way to a picture.',
      },
    ],
    goingFurther: [
      {
        id: 'w2-cont',
        kind: 'learn',
        title: 'Continuous Distributions',
        unlockedBy: 'w2-drv',
      },
      {
        id: 'w2-sim',
        kind: 'code',
        title: 'Simulating Distributions',
        unlockedBy: 'w2-cont',
      },
      {id: 'w2-poisson', kind: 'learn', title: 'Poisson', unlockedBy: 'w2-drv'},
      {
        id: 'w2-multi',
        kind: 'learn',
        title: 'Multinomial',
        unlockedBy: 'w2-drv',
      },
      {
        id: 'w2-vision',
        unlockedBy: 'w2-ev',
        kind: 'code',
        title: 'Vision Test Models',
      },
    ],
  },
  {
    id: 'w3',
    title: 'Fitting Models',
    label: 'Week 3',
    date: 'Oct 23',
    instructor: 'Chris',
    main: [
      {
        id: 'w3-mle',
        kind: 'learn',
        title: 'MLE + Gradient Ascent',
        blurb: 'One goal, two methods: solve it or climb it.',
      },
      {id: 'w3-cells', kind: 'code', title: 'Cell Tracking MLE'},
      {id: 'w3-pyramid', kind: 'code', title: 'Pyramid MLE'},
    ],
    goingFurther: [
      {
        id: 'w3-normal',
        kind: 'learn',
        title: 'Normal + CLT',
        unlockedBy: 'w3-mle',
      },
      {
        id: 'w3-dial',
        unlockedBy: 'w3-mle',
        kind: 'code',
        title: 'LLM Confidence Dial',
        blurb: 'Temperature scaling, by hand.',
      },
      {
        id: 'w3-visionmle',
        kind: 'code',
        title: 'Vision Test MLE',
        unlockedBy: 'w3-mle',
      },
    ],
  },
  {
    id: 'w4',
    title: 'Learning from Data',
    label: 'Week 4',
    date: 'Oct 30',
    instructor: 'Mehran',
    main: [
      {id: 'w4-mlsetup', kind: 'learn', title: 'The Machine Learning Setup'},
      {
        id: 'w4-logreg',
        kind: 'code',
        title: 'Debug Logistic Regression',
        blurb: 'Several solutions, some wrong. Which, and why?',
      },
    ],
    goingFurther: [
      {
        id: 'w4-glotp',
        unlockedBy: 'w4-mlsetup',
        kind: 'learn',
        title: 'General Law of Total Probability',
      },
      {
        id: 'w4-infer',
        kind: 'learn',
        title: 'Inference',
        unlockedBy: 'w4-glotp',
      },
    ],
  },
  {
    id: 'w5',
    title: 'Neural Networks',
    label: 'Week 5',
    date: 'Nov 6',
    instructor: 'Chris',
    main: [
      {
        id: 'w5-nnvis',
        kind: 'code',
        title: 'Visualize Your Neural Network',
      },
    ],
    goingFurther: [
      {
        id: 'w5-linreg',
        kind: 'learn',
        title: 'Linear Regression',
        unlockedBy: 'w5-nnvis',
      },
    ],
  },
  {
    id: 'w6',
    title: 'Trusting Predictions',
    label: 'Week 6',
    date: 'Nov 13',
    instructor: 'Chris',
    main: [
      {id: 'w6-calib', kind: 'learn', title: 'Calibration (Polymarket)'},
      {id: 'w6-cv', kind: 'learn', title: 'Cross Validation'},
      {id: 'w6-fair', kind: 'learn', title: 'Fairness'},
    ],
    goingFurther: [],
  },
];

/** The week the prototypes treat as "now". */
export const PROTOTYPE_CURRENT_WEEK_ID = 'w2';

/** Items the prototypes start with already completed. */
export const PROTOTYPE_INITIAL_COMPLETED: readonly string[] = [
  'w0-prob',
  'w0-cond',
  'w0-textdet',
  'w1-axioms',
  'w1-indep',
  'w1-monkeys',
  'w1-lotp',
  'w2-drv',
];
